import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Polygon, Polyline, Tooltip } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import {
  getGeometryColor,
  getGeometryLatLngs,
  hasRenderableGeometry,
  isClosedGeometryType,
} from 'src/utils/position-geometry'
import { IPosition } from '../../../../../generated-types'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import { grayedOpacity, opacity, geometryFillOpacity } from '../constraints'

interface IGeometryPositionProps {
  position: IPosition
  navigateToPosition?: (positionId: number) => void
}

export const GeometryPosition: React.FC<IGeometryPositionProps> = memo(({ position, navigateToPosition }) => {
  const showHiddenPositions = useShowHiddenPositions()
  const { t } = useTranslation(['mooring-layout'])
  // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
  if (position.isHidden && !showHiddenPositions) return <></>
  if (!hasRenderableGeometry(position.geometry, position.type)) return <></>
  if (position.isHidden) position = { ...position, isTransparent: true }

  const latLngs = getGeometryLatLngs(position.geometry)
  const color = getGeometryColor(position.geometry, position.type)
  const isClosed = isClosedGeometryType(position.type)
  const pane = position.isWorkAssignmentPosition
    ? isClosed
      ? 'polygonUpdated'
      : 'electricityLineUpdated'
    : isClosed
    ? 'polygon'
    : 'electricityLine'

  const pathOptions = {
    color,
    weight: isClosed ? 2.5 : 4,
    opacity: position.isTransparent ? grayedOpacity : opacity,
    dashArray: isClosed ? '7 5' : undefined,
    fillColor: color,
    fillOpacity: isClosed ? geometryFillOpacity : 0,
    pane,
  }

  const typeLabel = isClosed ? t('type-recapture-zone') : t('type-power-line')
  const label = position.reference?.trim() ? position.reference : `${typeLabel} ${getPositionName(position)}`
  const eventHandlers = navigateToPosition ? { click: () => navigateToPosition(position.id) } : {}
  const tooltip = (
    <Tooltip sticky>
      <strong data-test-id="map-hover-tooltip">{label}</strong>
    </Tooltip>
  )

  return isClosed ? (
    <Polygon key={position.id} positions={latLngs} pathOptions={pathOptions} pane={pane} eventHandlers={eventHandlers}>
      {tooltip}
    </Polygon>
  ) : (
    <Polyline key={position.id} positions={latLngs} pathOptions={pathOptions} pane={pane} eventHandlers={eventHandlers}>
      {tooltip}
    </Polyline>
  )
})
